/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Title from "./ui/Title";
import Text from "./ui/Text";
import Button from "./ui/Button";
import { useTranslate } from "../hooks/useTranslate";

const ErrorContainer = styled.div`
  display: grid;
  place-items: center;
  gap: 16px;
  padding: 32px 24px;
  text-align: center;
  min-height: 200px;
`;

export const WidgetErrorFallback = ({ resetErrorBoundary }) => {
  const { _t } = useTranslate();
  return (
    <ErrorContainer>
      <Title size="h3">{_t("Something went wrong")}</Title>
      <Text>{_t("An unexpected error occurred. Please try again.")}</Text>
      <Button variant="primary" onClick={resetErrorBoundary}>{_t("Try again")}</Button>
    </ErrorContainer>
  );
};

export const RouteErrorFallback = ({ resetErrorBoundary }) => {
  const { _t } = useTranslate();
  return (
    <ErrorContainer>
      <Title size="h3">{_t("Something went wrong")}</Title>
      <Text>{_t("This page encountered an error.")}</Text>
      <Button variant="primary" onClick={resetErrorBoundary}>{_t("Go back")}</Button>
    </ErrorContainer>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[SimplyBookWidget] Error caught by boundary:", error, errorInfo);
  }

  resetErrorBoundary() {
    if (this.props.onReset) {
      this.props.onReset();
    }
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;
      
      return <Fallback error={this.state.error} resetErrorBoundary={this.resetErrorBoundary} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
